import {
    ExpandedState,
    RowSelectionState,
    flexRender,
    getCoreRowModel,
    getExpandedRowModel,
    useReactTable
} from '@tanstack/react-table';
import { StandardLonghandProperties } from 'csstype';
import {
    ArrowDown,
    ArrowUp,
    ArrowUpDown,
    ChevronDown,
    ChevronUp
} from 'lucide-react';
import { observer } from 'mobx-react';
import React from 'react';
import { WrapRequest } from 'wrap-request';
import { Alert, AlertDescription } from '@components/ui/alert';
import { Button } from '@components/ui/button';
import { Checkbox } from '@components/ui/checkbox';
import { Skeleton } from '@components/ui/skeleton';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow
} from '@components/ui/table';
import { cn } from '@core/utils/cn';
import { ContextMenu, ContextMenuOption } from '../context-menu';
import { ReactColumn, SortingRule, toColumnDef } from './column-adapter';
import { Pagination } from './pagination';
import { SearchFilter } from './search-filter';

export type {
    CellProps,
    Column,
    ReactColumn,
    RowLike,
    SortingRule
} from './column-adapter';
export { useSelectionChange } from './selection-change.hook';
export { convertRecordsToArray } from './utils';

interface ObjectWithId {
    id: number;
    [key: string]: any;
}

export interface ResultPageDto<T = any> {
    content: T[];
    number?: number;
    size?: number;
    totalElements?: number;
}

interface TableProps<T extends ObjectWithId[] = ObjectWithId[]> {
    className?: string;
    columns: ReactColumn<T[0]>[];
    items: WrapRequest<ResultPageDto<T[0]>>;
    contextMenu?: ContextMenuOption<T[0]>[];
    emptyText?: string;
    stickyHeader?: boolean;
    canEdit?: boolean;
    initialSearch?: string;
    searchPlaceholder?: string;
    sorting?: SortingRule;
    rowsPerPageOptions?: number[];
    tableLayout?: StandardLonghandProperties['tableLayout'];
    cellPadding?: number;
    dense?: boolean;
    renderRowSubComponent?: ({ row }: { row: T[0] }) => JSX.Element;
    onRowClick?(row: T[0]): void;
    onSortingChange?(sorting?: SortingRule): void;
    onSearchChange?(value?: string): void;
    onChangePage?(value: number): void;
    onPageSizeChange?(pageSize: number): void;
    onSelectionChange?(selection: Record<string, boolean>): void;
    topBarAction?: React.ReactNode;
}

const FullWidthCell = ({
    colSpan,
    children
}: {
    colSpan: number;
    children: React.ReactNode;
}) => (
    <TableRow className="hover:bg-transparent">
        <TableCell colSpan={colSpan} className="py-6 text-center">
            {children}
        </TableCell>
    </TableRow>
);

const SortIcon = ({ sorting }: { sorting?: SortingRule }) => {
    if (!sorting) {
        return <ArrowUpDown className="size-3.5 opacity-50" />;
    }
    return sorting.desc ? (
        <ArrowDown className="size-3.5" />
    ) : (
        <ArrowUp className="size-3.5" />
    );
};

export const TableComponent = observer(
    <T extends any[]>({
        className,
        columns,
        items,
        contextMenu,
        stickyHeader,
        emptyText = 'No entries',
        canEdit = true,
        searchPlaceholder,
        onChangePage,
        onPageSizeChange,
        onSearchChange,
        onSortingChange,
        onSelectionChange,
        topBarAction,
        sorting,
        rowsPerPageOptions = [15, 30, 60, 100],
        initialSearch,
        cellPadding = 16,
        tableLayout = 'fixed',
        dense = false,
        renderRowSubComponent,
        onRowClick
    }: TableProps<T>) => {
        const {
            $: { content, size = 0, totalElements = 0, number: currentPage = 0 }
        } = items;

        const selectable = Boolean(onSelectionChange);
        const showContextMenu = Boolean(canEdit && contextMenu?.length);

        const columnDefs = React.useMemo(() => {
            const defs = columns.map((column, index) =>
                toColumnDef(column as ReactColumn<object>, index)
            );
            if (selectable) {
                defs.unshift({
                    id: 'selection',
                    enableSorting: false,
                    header: ({ table }) => (
                        <Checkbox
                            aria-label="Select all"
                            checked={
                                table.getIsAllRowsSelected() ||
                                (table.getIsSomeRowsSelected() &&
                                    'indeterminate')
                            }
                            onCheckedChange={(value) =>
                                table.toggleAllRowsSelected(value === true)
                            }
                        />
                    ),
                    cell: ({ row }) => (
                        <Checkbox
                            aria-label="Select row"
                            checked={row.getIsSelected()}
                            onCheckedChange={(value) =>
                                row.toggleSelected(value === true)
                            }
                            onClick={(e) => e.stopPropagation()}
                        />
                    ),
                    meta: { style: { width: 40 } }
                });
            }
            return defs;
        }, [columns, selectable]);

        const [rowSelection, setRowSelection] =
            React.useState<RowSelectionState>({});
        const [expanded, setExpanded] = React.useState<ExpandedState>({});

        const table = useReactTable({
            data: (content ?? []) as object[],
            columns: columnDefs,
            state: { rowSelection, expanded },
            onRowSelectionChange: setRowSelection,
            onExpandedChange: setExpanded,
            enableRowSelection: selectable,
            manualPagination: true,
            manualSorting: true,
            getCoreRowModel: getCoreRowModel(),
            getExpandedRowModel: getExpandedRowModel(),
            getRowId: (row) => String((row as ObjectWithId).id)
        });

        React.useEffect(() => {
            onSelectionChange?.(rowSelection);
        }, [rowSelection]);

        const colSpan =
            columnDefs.length +
            (showContextMenu ? 1 : 0) +
            (renderRowSubComponent ? 1 : 0);

        const handleSortingChange = (id: string) => {
            if (sorting?.id === id) {
                return onSortingChange?.({ id, desc: !sorting.desc });
            }
            onSortingChange?.({ id, desc: true });
        };

        const showPagination = totalElements > 15;
        const showTopBar =
            showPagination ||
            Boolean(onSearchChange) ||
            selectable ||
            Boolean(topBarAction);

        const minTableWidth = columnDefs.reduce(
            (total, def) => {
                const width = def.meta?.style?.width;
                return total + (typeof width === 'number' ? width : 170);
            },
            showContextMenu ? 60 : 0
        );

        const cellStyle = (style?: React.CSSProperties) => ({
            ...(dense ? {} : { padding: cellPadding }),
            ...style
        });

        // a declared width is a size, not a floor: without pinning it the
        // browser hands surplus table width to the widest declared column
        const headerStyle = (style?: React.CSSProperties) => {
            const base = cellStyle(style);
            return typeof base.width === 'number'
                ? { ...base, minWidth: base.width, maxWidth: base.width }
                : base;
        };

        return (
            <div
                data-table-root=""
                className={cn(
                    'w-full overflow-auto rounded-xl border bg-card',
                    className
                )}
            >
                {showTopBar && (
                    <div className="flex flex-wrap items-center justify-between gap-3 border-b px-4 py-2">
                        <div className="flex grow items-center gap-3">
                            {onSearchChange && (
                                <SearchFilter
                                    placeholder={searchPlaceholder}
                                    initialValue={initialSearch}
                                    onChange={onSearchChange}
                                />
                            )}
                        </div>
                        {showPagination && (
                            <Pagination
                                count={totalElements}
                                page={currentPage}
                                pageSize={size}
                                rowsPerPageOptions={rowsPerPageOptions}
                                onPageChange={(page) => onChangePage?.(page)}
                                onPageSizeChange={(pageSize) =>
                                    onPageSizeChange?.(pageSize)
                                }
                            />
                        )}
                        {topBarAction && (
                            <div className="flex items-center">
                                {topBarAction}
                            </div>
                        )}
                    </div>
                )}
                <Table style={{ tableLayout, minWidth: minTableWidth }}>
                    <TableHeader
                        className={cn(
                            stickyHeader && 'sticky top-14 z-10 bg-card'
                        )}
                    >
                        {table.getHeaderGroups().map((headerGroup) => (
                            <TableRow key={headerGroup.id}>
                                {renderRowSubComponent && (
                                    <TableHead style={{ width: 50 }} />
                                )}
                                {headerGroup.headers.map((header) => {
                                    const canSort =
                                        header.column.getCanSort() &&
                                        Boolean(onSortingChange);
                                    const active =
                                        sorting?.id === header.column.id
                                            ? sorting
                                            : undefined;
                                    return (
                                        <TableHead
                                            key={header.id}
                                            style={headerStyle(
                                                header.column.columnDef.meta
                                                    ?.style
                                            )}
                                            className={cn(
                                                'font-semibold',
                                                dense && 'py-1'
                                            )}
                                        >
                                            {canSort ? (
                                                <button
                                                    type="button"
                                                    className="inline-flex items-center gap-1"
                                                    onClick={() =>
                                                        handleSortingChange(
                                                            header.column.id
                                                        )
                                                    }
                                                >
                                                    {flexRender(
                                                        header.column.columnDef
                                                            .header,
                                                        header.getContext()
                                                    )}
                                                    <SortIcon
                                                        sorting={active}
                                                    />
                                                </button>
                                            ) : (
                                                flexRender(
                                                    header.column.columnDef
                                                        .header,
                                                    header.getContext()
                                                )
                                            )}
                                        </TableHead>
                                    );
                                })}
                                {showContextMenu && (
                                    <TableHead style={{ width: 60 }} />
                                )}
                            </TableRow>
                        ))}
                    </TableHeader>
                    <TableBody>
                        {items.match({
                            error: (e) => (
                                <FullWidthCell colSpan={colSpan}>
                                    <Alert variant="destructive">
                                        <AlertDescription>
                                            {e.message}
                                        </AlertDescription>
                                    </Alert>
                                </FullWidthCell>
                            ),
                            empty: () => (
                                <FullWidthCell colSpan={colSpan}>
                                    <span className="text-muted-foreground">
                                        {emptyText}
                                    </span>
                                </FullWidthCell>
                            ),
                            loading: () => (
                                <>
                                    {[0, 1, 2].map((i) => (
                                        <TableRow
                                            key={i}
                                            className="hover:bg-transparent"
                                        >
                                            <TableCell colSpan={colSpan}>
                                                <Skeleton className="h-5 w-full" />
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </>
                            ),
                            fetched: () => {
                                const rows = table.getRowModel().rows;
                                if (rows.length === 0) {
                                    return (
                                        <FullWidthCell colSpan={colSpan}>
                                            <span className="text-muted-foreground">
                                                {emptyText}
                                            </span>
                                        </FullWidthCell>
                                    );
                                }
                                return rows.map((row) => {
                                    const level = (row.original as any)
                                        .level as number | undefined;
                                    return (
                                        <React.Fragment key={row.id}>
                                            <TableRow
                                                className={cn(
                                                    onRowClick &&
                                                        'cursor-pointer',
                                                    level === 2 &&
                                                        'bg-muted/30 [&>td:first-child]:pl-7',
                                                    renderRowSubComponent &&
                                                        row.getIsExpanded() &&
                                                        'border-b-0'
                                                )}
                                                onClick={() =>
                                                    onRowClick?.(
                                                        row.original as T[0]
                                                    )
                                                }
                                            >
                                                {renderRowSubComponent && (
                                                    <TableCell
                                                        style={{ width: 50 }}
                                                        onClick={(event) => {
                                                            event.stopPropagation();
                                                            row.toggleExpanded();
                                                        }}
                                                    >
                                                        <Button
                                                            variant="ghost"
                                                            size="icon-sm"
                                                            className="expand-toggle"
                                                            aria-label={
                                                                row.getIsExpanded()
                                                                    ? 'Collapse row'
                                                                    : 'Expand row'
                                                            }
                                                        >
                                                            {row.getIsExpanded() ? (
                                                                <ChevronUp />
                                                            ) : (
                                                                <ChevronDown />
                                                            )}
                                                        </Button>
                                                    </TableCell>
                                                )}
                                                {row
                                                    .getVisibleCells()
                                                    .map((cell) => (
                                                        <TableCell
                                                            key={cell.id}
                                                            style={{
                                                                whiteSpace:
                                                                    'nowrap',
                                                                textOverflow:
                                                                    'ellipsis',
                                                                overflow:
                                                                    'hidden',
                                                                ...cellStyle(
                                                                    cell.column
                                                                        .columnDef
                                                                        .meta
                                                                        ?.style
                                                                )
                                                            }}
                                                            className={cn(
                                                                dense && 'py-1'
                                                            )}
                                                        >
                                                            {flexRender(
                                                                cell.column
                                                                    .columnDef
                                                                    .cell,
                                                                cell.getContext()
                                                            )}
                                                        </TableCell>
                                                    ))}
                                                {showContextMenu && (
                                                    <TableCell
                                                        style={{ width: 60 }}
                                                        className="text-right"
                                                    >
                                                        <ContextMenu
                                                            items={contextMenu!.map(
                                                                (menu) => ({
                                                                    ...menu,
                                                                    value: row.original
                                                                })
                                                            )}
                                                        />
                                                    </TableCell>
                                                )}
                                            </TableRow>
                                            {renderRowSubComponent &&
                                                row.getIsExpanded() && (
                                                    <TableRow className="hover:bg-transparent">
                                                        <TableCell
                                                            colSpan={colSpan}
                                                            className="bg-muted/20"
                                                        >
                                                            {renderRowSubComponent(
                                                                {
                                                                    row: row.original as T[0]
                                                                }
                                                            )}
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                        </React.Fragment>
                                    );
                                });
                            }
                        }) ?? (
                            <FullWidthCell colSpan={colSpan}>
                                <span className="text-muted-foreground">
                                    {emptyText}
                                </span>
                            </FullWidthCell>
                        )}
                    </TableBody>
                </Table>
            </div>
        );
    }
);
